'use client';

import {
  IconAdjustmentsHorizontal,
  IconChevronDown,
  IconX,
} from '@tabler/icons-react';
import { useTranslations } from 'i11n';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Badge, Button, Popover, PopoverContent, PopoverTrigger, cn } from 'ui';
import { countFilterValues } from '../utils/countFilterValues';

export interface FilterCategory<TKey extends string> {
  key: TKey;
  count: number;
  render: () => ReactNode;
}

export const buildFilterCategories = <
  TKey extends string,
  TValues extends object,
>({
  keys,
  values,
  paths,
  panels,
}: {
  keys: readonly TKey[];
  values: TValues;
  paths: Record<TKey, readonly (keyof TValues)[]>;
  panels: Record<TKey, () => ReactNode>;
}): FilterCategory<TKey>[] =>
  keys.map((key) => ({
    key,
    count: countFilterValues(paths[key].map((path) => values[path])),
    render: panels[key],
  }));

export const getActiveFilterKeys = <TKey extends string>(
  categories: FilterCategory<TKey>[],
) =>
  categories
    .filter((category) => category.count > 0)
    .map((category) => category.key);

const CountBadge = ({ count }: { count: number }) => (
  <Badge className='h-5 min-w-5 justify-center rounded-full px-1.5 text-[11px]'>
    {count}
  </Badge>
);

const FilterCategoryButton = <TKey extends string>({
  category,
  active,
  onSelect,
}: {
  category: FilterCategory<TKey>;
  active: boolean;
  onSelect: (key: TKey) => void;
}) => {
  const t = useTranslations('filters');

  const select = useCallback(
    () => onSelect(category.key),
    [onSelect, category.key],
  );

  return (
    <button
      type='button'
      onClick={select}
      className={cn(
        'flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors hover:bg-accent',
        {
          'bg-primary/10 font-medium text-primary hover:bg-primary/10': active,
        },
      )}
    >
      <span className='truncate'>{t(category.key)}</span>
      {category.count > 0 && <CountBadge count={category.count} />}
    </button>
  );
};

export const FiltersPanel = <TKey extends string>({
  categories,
  onClearAll,
  className,
}: {
  categories: FilterCategory<TKey>[];
  onClearAll: () => void;
  className?: string;
}) => {
  const t = useTranslations();

  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<TKey>();
  const [contentRemountKey, setContentRemountKey] = useState(0);

  const total = useMemo(
    () => categories.reduce((sum, category) => sum + category.count, 0),
    [categories],
  );

  const activeCategory = useMemo(
    () =>
      categories.find((category) => category.key === activeKey) ??
      categories.at(0),
    [categories, activeKey],
  );

  const clearAll = useCallback(() => {
    onClearAll();
    setContentRemountKey((key) => key + 1);
  }, [onClearAll]);

  const close = useCallback(() => setOpen(false), []);

  return (
    <div className='flex flex-row items-center gap-2'>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            className={cn(
              'h-10 gap-2 border-outline',
              { 'border-primary/40 bg-primary/5 text-primary': total > 0 },
              className,
            )}
          >
            <IconAdjustmentsHorizontal className='h-4 w-4 flex-shrink-0' />
            {t('filtersPanel.trigger')}
            {total > 0 && <CountBadge count={total} />}
            <IconChevronDown className='h-4 w-4 flex-shrink-0 opacity-60' />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align='start'
          className='w-[36rem] max-w-[calc(100vw-2rem)] p-0'
        >
          <div className='grid h-[23rem] grid-cols-[11rem_1fr]'>
            <nav className='flex flex-col gap-1 overflow-y-auto border-r p-2'>
              {categories.map((category) => (
                <FilterCategoryButton
                  key={category.key}
                  category={category}
                  active={category.key === activeCategory?.key}
                  onSelect={setActiveKey}
                />
              ))}
            </nav>

            <div
              key={`${activeCategory?.key}-${contentRemountKey}`}
              className='min-w-0 overflow-y-auto p-2'
            >
              {activeCategory?.render()}
            </div>
          </div>

          <div className='flex flex-row items-center justify-between border-t p-2'>
            <Button
              variant='ghost'
              size='sm'
              disabled={total === 0}
              onClick={clearAll}
            >
              {t('filtersPanel.clearAll')}
            </Button>
            <Button size='sm' onClick={close}>
              {t('filtersPanel.done')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {total > 0 && (
        <Button className='!w-min' variant='ghost' onClick={clearAll}>
          <IconX className='mr-2 h-4 w-4' /> {t('filters.clear')}
        </Button>
      )}
    </div>
  );
};
